import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquarePlus, Send, MessageCircle } from 'lucide-react'
import { toast } from 'sonner'
import { PublicLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/auth-store'
import { getSuggestions, submitSuggestion } from './api'
import type { Suggestion } from './types'

function formatTime(unix: number) {
  if (!unix) return ''
  const d = new Date(unix * 1000)
  return d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function SuggestionCard({ item }: { item: Suggestion }) {
  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        <Avatar className="size-9">
          <AvatarFallback>{item.username?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{item.username || '匿名用户'}</span>
          <span className="text-xs text-muted-foreground">{formatTime(item.created_at)}</span>
        </div>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{item.content}</p>
        {item.reply && (
          <div className="mt-3 rounded-md bg-muted p-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">管理员回复</p>
            <p className="whitespace-pre-wrap text-sm">{item.reply}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function SuggestionWall() {
  const { auth } = useAuthStore()
  const [content, setContent] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['suggestions'],
    queryFn: () => getSuggestions(1, 50),
  })

  const mutation = useMutation({
    mutationFn: (text: string) => submitSuggestion(text),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('提交成功，感谢您的反馈！')
        setContent('')
        queryClient.invalidateQueries({ queryKey: ['suggestions'] })
      } else {
        toast.error(res.message || '提交失败')
      }
    },
    onError: () => {
      toast.error('提交失败，请稍后重试')
    },
  })

  const handleSubmit = () => {
    const text = content.trim()
    if (!text) {
      toast.error('请输入建议内容')
      return
    }
    if (text.length > 2000) {
      toast.error('内容不能超过2000字')
      return
    }
    mutation.mutate(text)
  }

  const list = data?.data?.list || []
  const total = data?.data?.total || 0
  const isAuthed = !!auth?.user

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex items-center gap-2">
          <MessageCircle className="size-6 text-primary" />
          <h1 className="text-2xl font-bold">建议墙</h1>
        </div>

        {isAuthed ? (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                <MessageSquarePlus className="size-4" />
                <span>分享您的反馈或建议</span>
              </div>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="在这里写下你的建议..."
                className="min-h-[100px] resize-y"
                maxLength={2000}
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{content.length}/2000</span>
                <Button onClick={handleSubmit} disabled={mutation.isPending || !content.trim()}>
                  <Send className="mr-1 size-4" />
                  {mutation.isPending ? '提交中...' : '提交'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6">
            <CardContent className="pt-4 text-center text-sm text-muted-foreground">
              请登录后提交建议
            </CardContent>
          </Card>
        )}

        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">共 {total} 条建议</p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <MessageCircle className="mx-auto mb-3 size-12 opacity-30" />
              <p>还没有建议，快来抢沙发吧！</p>
            </CardContent>
          </Card>
        ) : (
          <div>
            {list.map((item) => (
              <SuggestionCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
